// ============================================================================
// ActivityScopeSelector — workspace → team → member scope picker (R19)
//
// Replaces the static "workspace-wide" chip. The dropdown mirrors the role
// hierarchy top-down: the whole-workspace (or your-teams) option first, then
// the top-level roles as branches, each expandable into its child roles and
// the members holding it. Clicking a role scopes the page to that role AND
// everything beneath it; clicking a member scopes to that individual. The
// server re-validates every selection — this component only offers what
// activityScopeOptions said the viewer may see.
//
// Interaction notes: chevrons expand without selecting (so browsing never
// changes the page), top-level branches start expanded (the "primary branch"
// teams are one glance away), a type-to-filter box appears for larger teams,
// Escape and outside-click close, and touch targets stay ≥ 40px for mobile.
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import type { ActivityScopeMember, ActivityScopeRole } from '../../services/rbacService';
import { ACTIVITY_COLORS } from './activityTheme';

export type ActivityScopeValue =
  | { kind: 'all' }
  | { kind: 'role'; roleId: string }
  | { kind: 'member'; memberId: string };

export function scopeValueKey(value: ActivityScopeValue): string {
  if (value.kind === 'role') return `role:${value.roleId}`;
  if (value.kind === 'member') return `member:${value.memberId}`;
  return 'all';
}

const Wrap = styled.div`
  position: relative;
  display: inline-flex;
`;

const TriggerChip = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: ${ACTIVITY_COLORS.ink};
  background: ${(p) => (p.$open ? ACTIVITY_COLORS.gridline : ACTIVITY_COLORS.surface)};
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 999px;
  padding: 0.375rem 0.875rem;
  cursor: pointer;
  text-transform: lowercase;
  max-width: min(60vw, 280px);
  transition: background 0.15s ease;

  &:hover {
    background: ${ACTIVITY_COLORS.gridline};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Caret = styled.span<{ $open: boolean }>`
  flex: none;
  font-size: 0.5625rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
  transform: rotate(${(p) => (p.$open ? 180 : 0)}deg);
  transition: transform 0.15s ease;
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 60;
  width: min(340px, calc(100vw - 2rem));
  max-height: min(440px, 70vh);
  overflow-y: auto;
  background: ${ACTIVITY_COLORS.surface};
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  padding: 0.375rem;

  /* Phones: the chip can sit anywhere after the control bar wraps, so a
     chip-anchored panel could hang off the left edge. Pin it to the viewport
     as a sheet instead. */
  @media (max-width: 480px) {
    position: fixed;
    top: auto;
    left: 1rem;
    right: 1rem;
    bottom: 1rem;
    width: auto;
    max-height: 60vh;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${ACTIVITY_COLORS.border};
  border-radius: 6px;
  padding: 0.4375rem 0.625rem;
  font-size: 0.8125rem;
  margin-bottom: 0.375rem;
  color: ${ACTIVITY_COLORS.ink};
  background: ${ACTIVITY_COLORS.surface};

  &:focus {
    outline: none;
    border-color: ${ACTIVITY_COLORS.magnitude};
  }

  &::placeholder {
    color: ${ACTIVITY_COLORS.inkMuted};
  }
`;

const Row = styled.button<{ $depth: number; $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  width: 100%;
  min-height: 40px;
  border: none;
  border-radius: 7px;
  background: ${(p) => (p.$selected ? 'rgba(198, 167, 94, 0.16)' : 'transparent')};
  color: ${ACTIVITY_COLORS.ink};
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;
  padding: 0.375rem 0.5rem;
  padding-left: ${(p) => 0.5 + p.$depth * 1.125}rem;

  &:hover {
    background: ${(p) => (p.$selected ? 'rgba(198, 167, 94, 0.2)' : ACTIVITY_COLORS.gridline)};
  }
`;

const Chevron = styled.span<{ $expanded: boolean; $visible: boolean }>`
  flex: none;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5625rem;
  color: ${ACTIVITY_COLORS.inkSecondary};
  visibility: ${(p) => (p.$visible ? 'visible' : 'hidden')};
  transform: rotate(${(p) => (p.$expanded ? 90 : 0)}deg);
  transition: transform 0.12s ease;
  border-radius: 4px;

  &:hover {
    background: ${ACTIVITY_COLORS.border};
  }
`;

const RowLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowMeta = styled.span`
  flex: none;
  font-size: 0.6875rem;
  color: ${ACTIVITY_COLORS.inkMuted};
  font-variant-numeric: tabular-nums;
`;

const MemberDot = styled.span`
  flex: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${ACTIVITY_COLORS.gridline};
  border: 1px solid ${ACTIVITY_COLORS.border};
  color: ${ACTIVITY_COLORS.inkSecondary};
  font-size: 0.5625rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: lowercase;
`;

const GroupNote = styled.div`
  font-size: 0.6875rem;
  color: ${ACTIVITY_COLORS.inkMuted};
  padding: 0.5rem 0.5rem 0.25rem;
  text-transform: lowercase;
`;

const EmptyNote = styled.div`
  font-size: 0.8125rem;
  color: ${ACTIVITY_COLORS.inkMuted};
  padding: 0.75rem 0.5rem;
  text-align: center;
`;

export function memberLabel(m: ActivityScopeMember): string {
  return m.displayName || m.email || m.memberId.slice(0, 12);
}

/**
 * Display-only label cleanup (Miles 2026-08-13): scope options must read as
 * actual words, never slug-ish coded tags — "workspace-wide" → "workspace
 * wide", "read-only" → "read only". Ids and server round-trips keep the raw
 * names; only what the user sees is humanized.
 */
export function humanizeScopeLabel(raw: string): string {
  return raw
    .replace(/[-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

interface Props {
  mode: 'team' | 'subtree';
  roles: ActivityScopeRole[];
  members: ActivityScopeMember[];
  value: ActivityScopeValue;
  onChange: (value: ActivityScopeValue) => void;
}

const ActivityScopeSelector: React.FC<Props> = ({ mode, roles, members, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const allLabel = mode === 'team' ? 'workspace wide' : 'your teams';

  const tree = useMemo(() => {
    const childrenOf = new Map<string, ActivityScopeRole[]>();
    const roots: ActivityScopeRole[] = [];
    for (const role of roles) {
      if (role.parentRoleIds.length === 0) {
        roots.push(role);
      } else {
        for (const parentId of role.parentRoleIds) {
          const list = childrenOf.get(parentId) ?? [];
          list.push(role);
          childrenOf.set(parentId, list);
        }
      }
    }
    const byName = (a: ActivityScopeRole, b: ActivityScopeRole) => a.name.localeCompare(b.name);
    roots.sort(byName);
    for (const list of childrenOf.values()) list.sort(byName);

    const membersOf = new Map<string, ActivityScopeMember[]>();
    const grouped = new Set<string>();
    for (const role of roles) {
      const list = members.filter((m) => m.roleIds.includes(role.roleId));
      list.sort((a, b) => memberLabel(a).localeCompare(memberLabel(b)));
      membersOf.set(role.roleId, list);
      for (const m of list) grouped.add(m.memberId);
    }
    const ungrouped = members
      .filter((m) => !grouped.has(m.memberId))
      .sort((a, b) => memberLabel(a).localeCompare(memberLabel(b)));

    // Effective member count per role = UNIQUE members across the role and
    // everything beneath it — the same population clicking the role scopes
    // to. Drives both the row's count and the empty-role hiding rule (Miles
    // 2026-08-13: an option that would just display zero must not be shown).
    // A role with no direct members but a populated child stays visible — it
    // is the path to that child. DFS memoized per role; visited-set guards
    // the inheritsFrom DAG (a role can hang under multiple parents).
    const subtreeMembersOf = new Map<string, Set<string>>();
    const collectSubtree = (roleId: string, trail: Set<string>): Set<string> => {
      const memoized = subtreeMembersOf.get(roleId);
      if (memoized) return memoized;
      const acc = new Set<string>((membersOf.get(roleId) ?? []).map((m) => m.memberId));
      if (!trail.has(roleId)) {
        trail.add(roleId);
        for (const child of childrenOf.get(roleId) ?? []) {
          for (const id of collectSubtree(child.roleId, trail)) acc.add(id);
        }
        trail.delete(roleId);
      }
      subtreeMembersOf.set(roleId, acc);
      return acc;
    };
    const subtreeCountOf = new Map<string, number>();
    for (const role of roles) {
      subtreeCountOf.set(role.roleId, collectSubtree(role.roleId, new Set()).size);
    }

    return { roots, childrenOf, membersOf, ungrouped, subtreeCountOf };
  }, [roles, members]);

  const subtreeCount = (roleId: string): number => tree.subtreeCountOf.get(roleId) ?? 0;

  // Top-level branches start expanded — the primary teams are one glance away.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setExpanded(new Set(tree.roots.map((r) => r.roleId)));
  }, [tree.roots]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const currentLabel = useMemo(() => {
    if (value.kind === 'role') {
      const name = roles.find((r) => r.roleId === value.roleId)?.name;
      return name ? humanizeScopeLabel(name) : 'team';
    }
    if (value.kind === 'member') {
      const m = members.find((x) => x.memberId === value.memberId);
      return m ? memberLabel(m) : 'member';
    }
    return allLabel;
  }, [value, roles, members, allLabel]);

  const select = (next: ActivityScopeValue) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  const toggleExpand = (roleId: string, expand?: boolean) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      const shouldExpand = expand ?? !next.has(roleId);
      if (shouldExpand) next.add(roleId);
      else next.delete(roleId);
      return next;
    });
  };

  const renderMemberRow = (m: ActivityScopeMember, depth: number) => {
    const label = memberLabel(m);
    const selected = value.kind === 'member' && value.memberId === m.memberId;
    return (
      <Row
        key={`m:${m.memberId}:${depth}`}
        $depth={depth}
        $selected={selected}
        role="option"
        aria-selected={selected}
        onClick={() => select({ kind: 'member', memberId: m.memberId })}
      >
        <MemberDot>{label.slice(0, 1)}</MemberDot>
        <RowLabel>{label}</RowLabel>
        {m.isOwner && <RowMeta>owner</RowMeta>}
      </Row>
    );
  };

  // `flat` renders the row alone — search results list matches without
  // dragging each match's whole expanded subtree (and duplicates) along.
  const renderRoleBranch = (
    role: ActivityScopeRole,
    depth: number,
    flat = false
  ): React.ReactNode => {
    // Empty subtrees are pruned at every level, not just the roots — a child
    // role with nobody in or beneath it is the same zero-row noise.
    const children = flat
      ? []
      : (tree.childrenOf.get(role.roleId) ?? []).filter((c) => subtreeCount(c.roleId) > 0);
    const roleMembers = flat ? [] : tree.membersOf.get(role.roleId) ?? [];
    const expandable = children.length > 0 || roleMembers.length > 0;
    const isExpanded = !flat && expanded.has(role.roleId);
    const selected = value.kind === 'role' && value.roleId === role.roleId;
    const count = subtreeCount(role.roleId);
    return (
      <React.Fragment key={role.roleId}>
        <Row
          $depth={depth}
          $selected={selected}
          role="option"
          aria-selected={selected}
          aria-expanded={expandable ? isExpanded : undefined}
          onClick={() => select({ kind: 'role', roleId: role.roleId })}
          onKeyDown={
            expandable
              ? (e) => {
                  // Keyboard parity with the chevron: → expands, ← collapses;
                  // Enter/Space keep selecting via the button default.
                  if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    toggleExpand(role.roleId, true);
                  } else if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    toggleExpand(role.roleId, false);
                  }
                }
              : undefined
          }
        >
          <Chevron
            $expanded={isExpanded}
            $visible={expandable}
            aria-hidden
            onClick={
              expandable
                ? (e) => {
                    e.stopPropagation();
                    toggleExpand(role.roleId);
                  }
                : undefined
            }
          >
            ▶
          </Chevron>
          <RowLabel>{humanizeScopeLabel(role.name)}</RowLabel>
          <RowMeta>
            {count} {count === 1 ? 'member' : 'members'}
          </RowMeta>
        </Row>
        {isExpanded && (
          <>
            {children.map((child) => renderRoleBranch(child, depth + 1))}
            {roleMembers.map((m) => renderMemberRow(m, depth + 1))}
          </>
        )}
      </React.Fragment>
    );
  };

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;
  // Search matches the humanized label too ("read only" finds "read-only"),
  // and empty subtrees stay hidden in results just like in the tree.
  const matchedRoles = searching
    ? roles.filter(
        (r) =>
          subtreeCount(r.roleId) > 0 &&
          (r.name.toLowerCase().includes(q) || humanizeScopeLabel(r.name).includes(q))
      )
    : [];
  const matchedMembers = searching
    ? members.filter((m) => memberLabel(m).toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q))
    : [];

  return (
    <Wrap ref={wrapRef}>
      <TriggerChip
        type="button"
        $open={open}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`activity scope: ${currentLabel}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{currentLabel}</span>
        <Caret $open={open}>▼</Caret>
      </TriggerChip>

      {open && (
        <Panel role="listbox" aria-label="choose whose activity to view">
          {members.length + roles.length > 8 && (
            <SearchInput
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="find a team or member…"
              aria-label="filter teams and members"
            />
          )}

          {searching ? (
            <>
              {matchedRoles.map((r) => renderRoleBranch(r, 0, true))}
              {matchedMembers.map((m) => renderMemberRow(m, 0))}
              {matchedRoles.length === 0 && matchedMembers.length === 0 && (
                <EmptyNote>nothing matches “{query.trim()}”.</EmptyNote>
              )}
            </>
          ) : (
            <>
              <Row
                $depth={0}
                $selected={value.kind === 'all'}
                role="option"
                aria-selected={value.kind === 'all'}
                onClick={() => select({ kind: 'all' })}
              >
                <Chevron $expanded={false} $visible={false}>
                  ▶
                </Chevron>
                <RowLabel>{allLabel}</RowLabel>
                <RowMeta>
                  {members.length} {members.length === 1 ? 'member' : 'members'}
                </RowMeta>
              </Row>
              {tree.roots
                .filter((role) => subtreeCount(role.roleId) > 0)
                .map((role) => renderRoleBranch(role, 0))}
              {tree.ungrouped.length > 0 && (
                <>
                  {tree.roots.length > 0 && (
                    <GroupNote>
                      {mode === 'team' ? 'members without a team role' : 'you'}
                    </GroupNote>
                  )}
                  {tree.ungrouped.map((m) => renderMemberRow(m, 0))}
                </>
              )}
            </>
          )}
        </Panel>
      )}
    </Wrap>
  );
};

export default ActivityScopeSelector;
